import { useAppContext } from '../../../context/AppContext';
import { postData } from '../../../services/postData';
import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import logo from '../../../assets/images/logo.webp';
import pasture from '../../../assets/images/pasture.jpg';

export const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { base_url, setToken } = useAppContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const redirectParam = searchParams.get('redirect');
    // Guest routes render this page in place, so a deep link such as
    // /project/:projectId stays in the address bar until login succeeds.
    const redirectTo = redirectParam
        || (location.pathname !== '/login' ? `${location.pathname}${location.search}` : '/');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            console.log('Logging in with email:', email);
            const response = await postData(null, `${base_url}/apis/admin/login`, {
                email,
                password
            });

            if (response && response.token) {
                setToken(response.token, rememberMe);
                console.log('Login successful!');
                navigate(redirectTo);
            } else {
                setError(response?.msg || 'Invalid email or password');
            }
        } catch (err) {
            setError(err?.response?.data?.msg || err?.msg || 'Login failed. Please try again.');
            console.error('Login error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-page">
            <section className="login-page__visual" aria-label="Welcome">
                <img
                    src={pasture}
                    alt="Cattle grazing under elevated solar arrays in a mountain pasture"
                    className="login-page__image"
                />
                <div className="login-page__visual-content">
                    <p className="login-page__visual-kicker">New here?</p>
                    <h2 className="login-page__visual-heading">
                        Start designing solar projects with confidence
                    </h2>
                    <p className="login-page__visual-copy">
                        Create a free portal account, or explore our tutorials library before you sign in.
                    </p>
                    <div className="login-page__ctas">
                        <Link to="/signup" className="btn btn-primary login-page__cta">
                            Create an account
                        </Link>
                        <Link to="/tutorials" className="btn login-page__cta login-page__cta--ghost">
                            View tutorials library
                        </Link>
                    </div>
                </div>
            </section>

            <section className="login-page__panel">
                <div className="login-page__panel-inner">
                    <div className="login-page__header">
                        <img
                            src={logo}
                            alt="Company Logo"
                            className="auth-page__logo"
                        />
                    </div>

                    <small className="auth-page__notice login-page__notice">
                        Please be aware that this portal is different from the MT Solar website. If you have previously made an account on the website, that account will not work for this portal.
                    </small>

                    <div className="login-page__card">
                        <h2 className="modal-title">Login</h2>

                        {error && (
                            <div className="form-error-banner">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="email" className="form-label">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    className="form-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    placeholder="Enter your email"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="password" className="form-label">
                                    Password
                                </label>
                                <div className="password-field">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        className="form-input"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        placeholder="Enter your password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="password-toggle"
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </button>
                                </div>
                            </div>

                            <div className="form-group form-group--checkbox-row">
                                <input
                                    type="checkbox"
                                    id="rememberMe"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="form-check-input"
                                />
                                <label htmlFor="rememberMe" className="form-label form-label--checkbox">
                                    Remember me
                                </label>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="submit"
                                    className="btn btn-primary btn--block"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Logging in...' : 'Login'}
                                </button>
                            </div>
                        </form>

                        <p className="auth-page__footer-link-row">
                            <Link to="/request-password-reset" className="text-link">
                                Forgot Password
                            </Link>
                        </p>

                        <div className="auth-page__footer">
                            <p className="auth-page__footer-p login-page__mobile-signup">
                                Don't have an account?{' '}
                                <Link to="/signup" className="text-link">
                                    Create one here
                                </Link>
                            </p>
                            <p className="auth-page__footer-p">
                                By logging in, you agree to our{' '}
                                <Link to="/terms" className="text-link">
                                    Terms of Service
                                </Link>
                                {' '}and{' '}
                                <Link to="/privacy" className="text-link">
                                    Privacy Policy
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};
